import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { UserPlus } from 'lucide-react';
import { AppLayout } from '@/components/layout/AppLayout';
import { AdminGuard } from '@/components/guards/AdminGuard';
import { PageHeader } from '@/components/layout/PageHeader';
import { UserTable } from '@/components/admin/UserTable';
import { AddUserDialog } from '@/components/admin/AddUserDialog';
import { EditUserRoleDialog } from '@/components/admin/EditUserRoleDialog';
import { RemoveUserDialog } from '@/components/admin/RemoveUserDialog';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorMessage } from '@/components/common/ErrorMessage';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { useAuth } from '@/hooks/useAuth';
import { usersApi, usersKeys } from '@/lib/api';
import type { ApplicationUser, CreateUserRequest, UpdateUserRoleRequest } from '@/types';

export const Route = createFileRoute('/admin/users')({
  component: AdminUsersPage,
});

function AdminUsersPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user: currentUser } = useAuth();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<ApplicationUser | null>(null);
  const [removingUser, setRemovingUser] = useState<ApplicationUser | null>(null);

  const {
    data: users,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: usersKeys.list(),
    queryFn: () => usersApi.list(),
  });

  const { mutate: createUser, isPending: isCreating } = useMutation({
    mutationFn: (data: CreateUserRequest) => usersApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: usersKeys.all });
      setIsAddDialogOpen(false);
      toast({
        title: 'User Added',
        description: 'The user has been added successfully.',
      });
    },
    onError: (err: Error) => {
      toast({
        title: 'Failed to Add User',
        description: err.message || 'An error occurred while adding the user.',
        variant: 'destructive',
      });
    },
  });

  const { mutate: updateUserRole, isPending: isUpdatingRole } = useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: UpdateUserRoleRequest }) =>
      usersApi.updateRole(userId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: usersKeys.all });
      setEditingUser(null);
      toast({
        title: 'Role Updated',
        description: 'The user role has been updated successfully.',
      });
    },
    onError: (err: Error) => {
      toast({
        title: 'Failed to Update Role',
        description: err.message || 'An error occurred while updating the role.',
        variant: 'destructive',
      });
    },
  });

  const { mutate: removeUser, isPending: isRemoving } = useMutation({
    mutationFn: (userId: string) => usersApi.remove(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: usersKeys.all });
      setRemovingUser(null);
      toast({
        title: 'User Removed',
        description: 'The user has been removed from the application.',
      });
    },
    onError: (err: Error) => {
      toast({
        title: 'Failed to Remove User',
        description: err.message || 'An error occurred while removing the user.',
        variant: 'destructive',
      });
    },
  });

  const handleAddUser = (data: CreateUserRequest) => {
    createUser(data);
  };

  const handleEditRole = (userId: string, data: UpdateUserRoleRequest) => {
    updateUserRole({ userId, data });
  };

  const handleRemoveUser = (userId: string) => {
    removeUser(userId);
  };

  return (
    <AdminGuard>
      <AppLayout>
        <div className="container max-w-5xl py-8">
          <PageHeader
            title="User Management"
            description="Manage application users and their roles"
            actions={
              <Button onClick={() => setIsAddDialogOpen(true)}>
                <UserPlus className="mr-2 h-4 w-4" />
                Add User
              </Button>
            }
          />

          {isLoading ? (
            <div className="flex items-center justify-center min-h-[400px]">
              <LoadingSpinner size="lg" label="Loading users..." />
            </div>
          ) : isError ? (
            <ErrorMessage
              title="Failed to Load Users"
              error={error}
              onRetry={() => refetch()}
            />
          ) : (
            <UserTable
              users={users ?? []}
              currentUserId={currentUser?.id ?? ''}
              onEditRole={setEditingUser}
              onRemove={setRemovingUser}
            />
          )}

          <AddUserDialog
            open={isAddDialogOpen}
            onOpenChange={setIsAddDialogOpen}
            onSubmit={handleAddUser}
            isSubmitting={isCreating}
          />

          <EditUserRoleDialog
            open={!!editingUser}
            onOpenChange={(open) => !open && setEditingUser(null)}
            user={editingUser}
            onSubmit={handleEditRole}
            isSubmitting={isUpdatingRole}
          />

          <RemoveUserDialog
            open={!!removingUser}
            onOpenChange={(open) => !open && setRemovingUser(null)}
            user={removingUser}
            onConfirm={handleRemoveUser}
            isRemoving={isRemoving}
          />

          <Toaster />
        </div>
      </AppLayout>
    </AdminGuard>
  );
}
